import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import DOMPurify from 'dompurify';
import Footer from 'components/Footer/Footer';
import { LeftNavigationBar } from 'screens/Main/components/LeftNavigationBar/LeftNavigationBar';
import { TopNavigationBar } from 'screens/Main/components/TopNavigationBar/TopNavigationBar';
import LoginPromptModal from 'components/LoginPromptModal/LoginPromptModal';
import { MainRoutesProps } from 'routes';
import { getLanguageDisplayName } from 'constants/languages';
import './ViewCardPage.css';
import { FormControl, MenuItem, Select } from '@mui/material';
import { Table, TableColumn } from 'screens/Dev/Table';
import MagicButton from 'components/MagicButton/MagicButton';
import { FoilCardImage } from 'components/FoilCardImage';
import { SwapHoriz } from '@mui/icons-material';
import Points from 'components/Points';
import { useGetCardByIdQuery, useGetCardByPrintingQuery, useGetCardPrintingsQuery } from 'store/services/cards/search/cardsSearchApi';
import { useGetCardWantersQuery } from 'store/services/cards/wanters/cardWantersApi';
import { buildCardUrl, getLegacyRedirectPath } from 'utils/cardUrlUtils';
import { pickAltNames } from 'utils/cardNames';
import { decimalToPoints } from 'utils/decimalUtils';
import { useUpdateAvatarMutation } from 'store/services/user/meApi';
import { toast } from 'react-toastify';
import { AssetUrls } from 'config/assets';
import { useAddCardToHaveListMutation } from 'store/services/collectionList/haveList/haveListApi';
import { CardCondition } from 'types/interfaces/condition/condition';
import { showCardAddedToHaveListSuccess, showError } from 'utils/toastNotifications';
import { SEOHelmet, BreadcrumbList } from 'components/SEO';
import { SetIcon } from 'components/SetIcon';
import { useIsAdmin } from 'hooks/useIsAdmin';
import { ImportSessionStorage } from 'utils/importSessionStorage';
import CardWantersTable from './components/CardWantersTable/CardWantersTable';

interface Props {
  currentTab: MainRoutesProps;
}

interface PrintData {
  id: number;
  scryfallID: string;
  name: string;
  setName: string;
  setCode?: string;
  setIconUrl?: string;
  collectorNumber: string;
  points: number | null;
  image: string;
}

interface LanguageOption {
  code: string;
  name: string;
  scryfallId?: string;
}

// Function to safely replace mana symbols with icons using backend URLs when available
const replaceManaSymbols = (text: string, manaSymbolsMap?: Map<string, string>): string => {
  if (!text) return '';
  
  // First, escape any HTML in the original text to prevent XSS
  const div = document.createElement('div');
  div.textContent = text;
  let escapedText = div.innerHTML;
  
  // Try to use manaSymbolsMap from backend if available, otherwise fallback to CDN URLs
  const manaSymbols: { [key: string]: string } = manaSymbolsMap ? 
    Object.fromEntries(manaSymbolsMap) : 
    {
      // Basic mana symbols - using CDN URLs
      '{W}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/W.svg',
      '{U}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/U.svg',
      '{B}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/B.svg',
      '{R}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/R.svg',
      '{G}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/G.svg',
      '{C}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/C.svg',
      // Generic mana symbols
      '{0}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/0.svg',
      '{1}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/1.svg',
      '{2}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/2.svg',
      '{3}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/3.svg',
      '{4}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/4.svg',
      '{5}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/5.svg',
      '{6}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/6.svg',
      '{7}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/7.svg',
      '{8}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/8.svg',
      '{9}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/9.svg',
      '{10}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/10.svg',
      '{11}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/11.svg',
      '{12}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/12.svg',
      '{13}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/13.svg',
      '{14}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/14.svg',
      '{15}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/15.svg',
      '{16}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/16.svg',
      '{17}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/17.svg',
      '{18}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/18.svg',
      '{19}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/19.svg',
      '{20}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/20.svg',
      '{X}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/X.svg',
      // Special symbols
      '{T}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/T.svg',
      '{Q}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/Q.svg',
      '{E}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/E.svg',
      '{S}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/S.svg',
      // Hybrid mana symbols
      '{W/U}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/WU.svg',
      '{W/B}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/WB.svg',
      '{U/B}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/UB.svg',
      '{U/R}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/UR.svg',
      '{B/R}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/BR.svg',
      '{B/G}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/BG.svg',
      '{R/G}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/RG.svg',
      '{R/W}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/RW.svg',
      '{G/W}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/GW.svg',
      '{G/U}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/GU.svg',
      // Phyrexian mana
      '{W/P}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/WP.svg',
      '{U/P}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/UP.svg',
      '{B/P}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/BP.svg',
      '{R/P}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/RP.svg',
      '{G/P}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/GP.svg',
      // Two-brid mana
      '{2/W}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/2W.svg',
      '{2/U}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/2U.svg',
      '{2/B}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/2B.svg',
      '{2/R}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/2R.svg',
      '{2/G}': 'https://trademagic-assets.s3.us-west-1.amazonaws.com/symbols/mana/2G.svg',
    };
  
  // Replace each mana symbol with an img tag
  let replacedText = escapedText;
  
  Object.entries(manaSymbols).forEach(([symbol, url]) => {
    const regex = new RegExp(symbol.replace(/[{}]/g, '\\$&'), 'g');
    replacedText = replacedText.replace(regex, 
      `<img src="${url}" alt="${symbol}" style="width: 16px; height: 16px; display: inline-block; vertical-align: middle; margin: 0 2px;" />`
    );
  });
  
  // Also replace line breaks
  replacedText = replacedText.replace(/\n/g, '<br /><br />');
  
  // Sanitize the final HTML to ensure safety
  // Only allow img tags with specific attributes and br tags
  return DOMPurify.sanitize(replacedText, {
    ALLOWED_TAGS: ['img', 'br'],
    ALLOWED_ATTR: ['src', 'alt', 'style'],
    ALLOW_DATA_ATTR: false
  });
};

// Thumbnail component for printing art crops with loading/error states
const PrintingArtThumbnail: React.FC<{
  scryfallId?: string;
  imageUrl: string;
  cardName: string;
  enFallbackScryfallId?: string;
}> = ({ scryfallId, imageUrl, cardName, enFallbackScryfallId }) => {
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const fallbackAttemptedRef = useRef(false);
  const enFallbackAttemptedRef = useRef(false);

  // Reset error state when enFallbackScryfallId becomes available after initial failure
  useEffect(() => {
    if (enFallbackScryfallId && hasError) {
      setHasError(false);
      setIsLoading(true);
      fallbackAttemptedRef.current = false;
      enFallbackAttemptedRef.current = false;
    }
  }, [enFallbackScryfallId, hasError]);

  const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const currentSrc = img.src;

    // Try fallback formats: art_crop -> small -> normal
    if (scryfallId && !fallbackAttemptedRef.current) {
      if (currentSrc.includes('/art_crop/')) {
        img.src = AssetUrls.cards.getCardImage(scryfallId, 'small');
        return;
      } else if (currentSrc.includes('/small/')) {
        img.src = AssetUrls.cards.getCardImage(scryfallId, 'normal');
        return;
      }
      fallbackAttemptedRef.current = true;
    }

    // All localized formats exhausted — try EN art_crop fallback
    if (enFallbackScryfallId && !enFallbackAttemptedRef.current) {
      enFallbackAttemptedRef.current = true;
      img.src = AssetUrls.cards.getCardImage(enFallbackScryfallId, 'art_crop');
      return;
    }

    setHasError(true);
    setIsLoading(false);
  };

  const handleLoad = () => {
    setIsLoading(false);
  };

  // If all fallbacks failed, show placeholder
  if (hasError) {
    return (
      <div className="printing-art-placeholder" title={cardName}>
        <span className="printing-art-placeholder-icon">?</span>
      </div>
    );
  }

  return (
    <div className="printing-art-wrapper">
      {isLoading && (
        <div className="printing-art-loading">
          <span className="printing-art-shimmer"></span>
        </div>
      )}
      <img
        src={imageUrl}
        alt={cardName}
        className={`printing-art ${isLoading ? 'printing-art-hidden' : ''}`}
        onError={handleError}
        onLoad={handleLoad}
        loading="lazy"
      />
    </div>
  );
};

export default function ViewCardPage({ currentTab }: Props) {
  const { cardId, setCode: paramSetCode, collectorNumber: paramCollectorNumber } = useParams<{
    cardId?: string;
    cardSlug?: string;
    setCode?: string;
    collectorNumber?: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Determine which lookup mode we're in
  const isSlugRoute = !!(paramSetCode && paramCollectorNumber);
  const isLegacyRoute = !!cardId && !isSlugRoute;

  // Auth state for login modal
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const { isAdmin } = useIsAdmin(!currentUser);
  const [showLoginModal, setShowLoginModal] = useState(false);

  // A logged-out viewer clicking Send on a wanter row: stash the current URL so auth
  // returns here, then open the existing login prompt.
  const handleRequireLogin = React.useCallback(() => {
    ImportSessionStorage.setReturnTo(location.pathname + location.search);
    setShowLoginModal(true);
  }, [location.pathname, location.search]);

  // Preserve navigation state from search results
  const fromSearch = location.state?.from === 'search';
  const returnPath = location.state?.returnPath;

  const [foilType, setFoilType] = useState<string>('nonfoil'); // Will be updated based on card availability
  const [updateAvatar, { isLoading: isUpdatingAvatar }] = useUpdateAvatarMutation();
  const [addCardToHaveList, { isLoading: isAddingToHaveList }] = useAddCardToHaveListMutation();

  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  const [language, setLanguage] = useState('en'); // Use language code instead of name
  const [displayedFaceIndex, setDisplayedFaceIndex] = useState(0); // Track which face is displayed for double-faced cards
  const [localizedImageFailed, setLocalizedImageFailed] = useState(false);

  // Mobile detection effect
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
      }
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Fetch card data — by setCode+collectorNumber (new slug route) or by UUID (legacy route)
  const {
    data: cardDataByPrinting,
    isLoading: isLoadingByPrinting,
    error: errorByPrinting,
  } = useGetCardByPrintingQuery(
    { setCode: paramSetCode || '', collectorNumber: paramCollectorNumber || '' },
    { skip: !isSlugRoute }
  );

  const {
    data: cardDataById,
    isLoading: isLoadingById,
    error: errorById,
  } = useGetCardByIdQuery(cardId || '', {
    skip: !isLegacyRoute
  });

  const cardData = isSlugRoute ? cardDataByPrinting : cardDataById;
  const isLoading = isSlugRoute ? isLoadingByPrinting : isLoadingById;
  const error = isSlugRoute ? errorByPrinting : errorById;

  // For legacy UUID routes, redirect English printings to the slug URL once card
  // data loads. Non-English printings stay on /cards/<uuid>: the slug route's
  // by-printing lookup is language-agnostic and would snap back to English.
  useEffect(() => {
    if (!isLegacyRoute || !cardDataById) return;
    const redirectPath = getLegacyRedirectPath(cardDataById);
    if (redirectPath) {
      navigate(redirectPath, { replace: true });
    }
  }, [isLegacyRoute, cardDataById, navigate]);

  // Determine the card identifier (scryfallId UUID) for secondary API calls.
  // Both by-id and by-printing endpoints return a FLAT response (MTGCard fields at top level).
  // Backend may emit `scryfallId` (lowercase d) while MTGCard declares `scryfallID` (uppercase D);
  // check both casings to handle either response.
  const cardIdentifier = isSlugRoute
    ? (cardDataByPrinting?.scryfallId || cardDataByPrinting?.scryfallID || '')
    : (cardId || '');

  // Fetch all printings of this card
  const {
    data: printingsData,
    isLoading: isLoadingPrintings
  } = useGetCardPrintingsQuery({ cardId: cardIdentifier }, {
    skip: !cardIdentifier
  });
  
  // Backend returns the card data directly (FLAT shape — see CardDetailResponse).
  const card: any = cardData;

  // Wanted-by count for the community stat line. Deliberately the SAME query + argument
  // shape CardWantersTable uses ({ oracleId }): RTK Query dedupes identical subscriptions
  // into one cache entry, so the stat and the table can never disagree on the same page.
  const { data: wantersData, isLoading: isLoadingWanters } = useGetCardWantersQuery(
    { oracleId: card?.oracleId },
    { skip: !card?.oracleId }
  );
  // When the card has no oracleId the wanters table is hidden, so a 0 here can't
  // contradict it; render the honest zero rather than hiding the segment.
  const wantedByCount = card?.oracleId ? wantersData?.totalWanters : 0;
  
  // Parse card faces for double-faced cards
  // Handle both string and object formats
  let cardFaces: any = null;
  if (card?.cardFaces) {
    if (typeof card.cardFaces === 'string') {
      try {
        cardFaces = JSON.parse(card.cardFaces);
      } catch (e) {
        cardFaces = null;
      }
    } else {
      cardFaces = card.cardFaces; // Already an object
    }
  }

  // Determine available foil types for the current card
  const getFoilTypes = React.useCallback(() => {
    const types: { value: string; label: string }[] = [];
    let hasNonFoil = true;
    let hasFoil = true;

    // After UUID migration, use printingsData for price availability
    const currentPrinting = printingsData?.find((p: any) => p.scryfallId === cardIdentifier);
    const sourceCard = currentPrinting || card;

    // Handle price as an array (as per MTGCard interface)
    const priceData = Array.isArray(sourceCard?.price) && sourceCard.price.length > 0
      ? sourceCard.price[0]
      : sourceCard?.price;

    // Check explicit foil/nonFoil flags if they exist
    if (sourceCard?.nonFoil !== undefined) {
      hasNonFoil = sourceCard.nonFoil;
    }
    if (sourceCard?.foil !== undefined) {
      hasFoil = sourceCard.foil;
    }

    // Also check price data to determine availability
    // Price data is more reliable - if we have price data, use it to override flags
    if (priceData) {
      // Check if non-foil price exists and is a valid value
      const hasNonFoilPrice = priceData.usd != null && Number(priceData.usd) > 0;
      const hasFoilPrice = priceData.usdFoil != null && Number(priceData.usdFoil) > 0;
      const hasEtchedPrice = priceData.usdEtched != null && Number(priceData.usdEtched) > 0;

      // If we have ANY price data, use it to determine availability
      // This handles etched-only cards correctly
      if (hasNonFoilPrice || hasFoilPrice || hasEtchedPrice) {
        hasNonFoil = hasNonFoilPrice;
        hasFoil = hasFoilPrice;
      }
    }

    // Add non-foil option if available
    if (hasNonFoil) {
      types.push({ value: 'nonfoil', label: 'Non-Foil' });
    }

    // Add foil option if available
    if (hasFoil) {
      types.push({ value: 'foil', label: 'Foil' });
    }

    // Check for special foil types like etched
    if (priceData?.usdEtched || sourceCard?.finishes?.includes('etched')) {
      types.push({ value: 'etched', label: 'Etched' });
    }

    // If no types were added, default to showing both
    if (types.length === 0) {
      types.push({ value: 'nonfoil', label: 'Non-Foil' });
      types.push({ value: 'foil', label: 'Foil' });
    }

    return types;
  }, [card, cardIdentifier, printingsData]);
  
  const availableFoilTypes = React.useMemo(() => getFoilTypes(), [getFoilTypes]);

  // Detect ★ foil counterpart (7th–10th Edition store foils as separate printings with ★ collector numbers)
  const starPair = React.useMemo(() => {
    if (!printingsData || !card) return null;
    const cn = card.collectorNumber || card.collector_number || '';
    const currentSetCode = (card.setCode || card.set_code || '').toLowerCase();
    if (!cn || !currentSetCode) return null;

    if (cn.endsWith('★')) {
      // Current is the foil ★ variant — find the base
      const baseCN = cn.replace(/★$/, '');
      const base = printingsData.find((p: any) =>
        (p.setCode || p.set_code || '').toLowerCase() === currentSetCode &&
        (p.collectorNumber || p.collector_number) === baseCN
      );
      return base ? { isStar: true, paired: base } : null;
    } else {
      // Current is the base — find the ★ variant
      const starCN = cn + '★';
      const star = printingsData.find((p: any) =>
        (p.setCode || p.set_code || '').toLowerCase() === currentSetCode &&
        (p.collectorNumber || p.collector_number) === starCN
      );
      return star ? { isStar: false, paired: star } : null;
    }
  }, [printingsData, card]);

  // Get available languages for the card
  const getAvailableLanguages = React.useCallback((): LanguageOption[] => {
    // Use backend-provided availableLanguages if present
    if (card?.availableLanguages && Array.isArray(card.availableLanguages) && card.availableLanguages.length > 0) {
      const languages = card.availableLanguages.map((entry: { lang: string; scryfallId: string }) => ({
        code: entry.lang.toLowerCase(),
        name: getLanguageDisplayName(entry.lang) || entry.lang.toUpperCase(),
        scryfallId: entry.scryfallId
      }));

      // Sort: English first, then alphabetically by display name
      languages.sort((a: LanguageOption, b: LanguageOption) => {
        if (a.code === 'en') return -1;
        if (b.code === 'en') return 1;
        return a.name.localeCompare(b.name);
      });

      return languages;
    }

    // Fallback: show only the current card's language
    const langCode = card?.lang?.toLowerCase() || 'en';
    return [{
      code: langCode,
      name: getLanguageDisplayName(langCode) || langCode.toUpperCase()
    }];
  }, [card]);
  
  const availableLanguages = React.useMemo(() => getAvailableLanguages(), [getAvailableLanguages]);

  // Derive EN fallback scryfallId for non-English cards with missing localized images
  const enScryfallId = React.useMemo(() => {
    const enEntry = availableLanguages.find((l: LanguageOption) => l.code === 'en');
    return enEntry?.scryfallId;
  }, [availableLanguages]);

  // Proactively use EN image when the card has no localized CDN image (avoids broken image flash)
  const useEnFallback = card?.lang !== 'en' && !!enScryfallId && (
    !card?.imageUris || localizedImageFailed
  );
  const displayScryfallId = useEnFallback ? enScryfallId : (card?.scryfallId || (card as any)?.scryfallID);

  // Sync language state to the card's actual language when navigating to a different card
  React.useEffect(() => {
    if (card?.lang) {
      setLanguage(card.lang.toLowerCase());
    }
    setLocalizedImageFailed(false);
  }, [cardIdentifier, card?.lang]);
  
  // Set the default foil type based on availability
  // This will run only when card ID changes (navigating to a different card)
  React.useEffect(() => {
    if (availableFoilTypes.length > 0) {
      // Only set default if the current foilType is not in available types
      const isCurrentTypeAvailable = availableFoilTypes.some(type => type.value === foilType);
      if (!isCurrentTypeAvailable) {
        // Default to nonfoil if available, otherwise use the first available option
        const hasNonFoil = availableFoilTypes.some(type => type.value === 'nonfoil');
        if (hasNonFoil) {
          setFoilType('nonfoil');
        } else {
          // If no non-foil version, default to the first available (likely foil)
          setFoilType(availableFoilTypes[0].value);
        }
      }
    }
  }, [cardIdentifier, availableFoilTypes, foilType]); // Reset only when card ID changes
  
  // Get the current price based on foil selection
  // Backend provides pointValue (non-foil, language-multiplied) and
  // foilPointValue (foil, language-multiplied) on card detail responses
  const getCurrentPrice = () => {
    // Priority 1: Backend-calculated point values (language-multiplied)
    if (foilType === 'foil' && card?.foilPointValue && card.foilPointValue > 0) {
      return card.foilPointValue;
    }
    if (card?.pointValue && card.pointValue > 0) {
      return card.pointValue;
    }

    // Priority 2: Fallback to raw price from printings data
    const currentPrinting = printingsData?.find((p: any) => p.scryfallId === cardIdentifier);
    const sourceCard = currentPrinting || card;

    const priceData = Array.isArray(sourceCard?.price) && sourceCard.price.length > 0
      ? sourceCard.price[0]
      : sourceCard?.price;

    if (priceData) {
      if (foilType === 'foil' && priceData.usdFoil !== null && priceData.usdFoil !== undefined) {
        const foilPoints = decimalToPoints(priceData.usdFoil);
        if (foilPoints && foilPoints > 0) {
          return foilPoints;
        }
      } else if (foilType === 'nonfoil' && priceData.usd !== null && priceData.usd !== undefined) {
        const nonFoilPoints = decimalToPoints(priceData.usd);
        if (nonFoilPoints && nonFoilPoints > 0) {
          return nonFoilPoints;
        }
      } else if (foilType === 'etched' && priceData.usdEtched !== null && priceData.usdEtched !== undefined) {
        const etchedPoints = decimalToPoints(priceData.usdEtched);
        if (etchedPoints && etchedPoints > 0) {
          return etchedPoints;
        }
      }
    }

    return null;
  };

  // Transform printings data for the table
  const printRows: PrintData[] = (printingsData || cardData?.printings || []).map((printing: any, index: number) => {
    let imageUrl = ''; // Will be handled by PrintingArtThumbnail fallback logic
    
    // First, try to get image from the printing itself
    if (printing.imageUris?.art_crop || printing.image_uris?.art_crop) {
      let url = printing.imageUris?.art_crop || printing.image_uris?.art_crop;
      // Clean any error text that backend might append (temporary fix for backend bug)
      if (url && url.includes('{failed')) {
        url = url.split('{failed')[0].trim();
      }
      imageUrl = url;
    } else if (printing.imageUris?.small || printing.image_uris?.small) {
      let url = printing.imageUris?.small || printing.image_uris?.small;
      // Clean any error text that backend might append (temporary fix for backend bug)
      if (url && url.includes('{failed')) {
        url = url.split('{failed')[0].trim();
      }
      imageUrl = url;
    }
    // If we have a Scryfall ID, construct the CDN URL
    else if (printing.scryfallId) {
      // Use our CDN to get the art_crop image
      imageUrl = AssetUrls.cards.getCardImage(printing.scryfallId, 'art_crop');
    }
    // If printing doesn't have images and this is the main card, use the main card's data
    else if (printing.id === (card?.id || 0)) {
      // This printing is the main card being viewed
      if (cardFaces && cardFaces.length > 0) {
        // For double-faced cards, use the first face's art_crop
        const firstFace = cardFaces[0];
        const faceImageUrl = firstFace.imageUris?.art_crop || 
                  firstFace.image_uris?.art_crop ||
                  firstFace.imageUris?.small ||
                  firstFace.image_uris?.small ||
                  imageUrl;
        // Convert Scryfall URLs to CDN URLs
        imageUrl = AssetUrls.cards.ensureCDNUrl(faceImageUrl);
      } else if (card?.imageUris) {
        // Single-faced card, use regular imageUris
        imageUrl = card.imageUris.art_crop || 
                  card.imageUris.small || 
                  imageUrl;
      }
    }
    
    // Calculate points - prefer non-foil price when available
    let points: number | null = null;
    // Handle price as an array (as per MTGCard interface)
    const printingPriceData = Array.isArray(printing.price) && printing.price.length > 0 ? printing.price[0] : printing.price;
    
    if (printingPriceData) {
      // Always prefer non-foil (usd) over foil (usdFoil) when available
      if (printingPriceData.usd !== null && printingPriceData.usd !== undefined) {
        const nonFoilPoints = decimalToPoints(printingPriceData.usd);
        if (nonFoilPoints !== null && nonFoilPoints > 0) {
          points = nonFoilPoints;
        }
      }
      // Only use foil price if non-foil is not available
      if (!points && printingPriceData.usdFoil !== null && printingPriceData.usdFoil !== undefined) {
        const foilPoints = decimalToPoints(printingPriceData.usdFoil);
        if (foilPoints !== null && foilPoints > 0) {
          points = foilPoints;
        }
      }
    }
    
    // Use pointValue as fallback if no price data
    // The backend might be temporarily storing price in pointValue field
    if (!points) {
      const fallback = printing.pointValue || printing.points || null;
      if (fallback && fallback > 0) {
        points = fallback;
      }
    }

    // For non-EN cards, override the active printing's price with card.pointValue
    // which has the language multiplier applied by the backend
    const printingScryfallId = printing.scryfallId || printing.scryfallID;
    if (card?.lang && card.lang !== 'en' && printingScryfallId === cardIdentifier && card?.pointValue && card.pointValue > 0) {
      points = card.pointValue;
    }

    return {
      id: printing.id,
      scryfallID: printing.scryfallId || printing.scryfallID,
      name: `${printing.setName || printing.set_name || ''} #${printing.collectorNumber || printing.collector_number || ''}`,
      setName: printing.setName || printing.set_name || '',
      setCode: printing.setCode || (printing as any).set_code || '',
      setIconUrl: printing.setIconUrl || (printing.setCode ? `https://d3dmfym8zvsq5m.cloudfront.net/sets/icons/${printing.setCode}.svg` : ''),
      collectorNumber: printing.collectorNumber || printing.collector_number || '',
      points: points,
      image: imageUrl
    };
  });

  const printColumns: TableColumn<PrintData>[] = [
    {
      label: 'Prints',
      key: 'name',
      render: (row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <PrintingArtThumbnail
            scryfallId={row.scryfallID}
            imageUrl={row.image}
            cardName={row.name}
            enFallbackScryfallId={card?.lang !== 'en' ? enScryfallId : undefined}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <SetIcon setIconUrl={row.setIconUrl} setName={row.setName} />
            <span>
              {row.setName}
              {row.collectorNumber && (
                <span style={{ color: '#888', marginLeft: '4px' }}>
                  #{row.collectorNumber.endsWith('★') ? row.collectorNumber.replace(/★$/, '') : row.collectorNumber}
                </span>
              )}
              {row.collectorNumber?.endsWith('★') && (
                <span style={{
                  marginLeft: '4px',
                  fontSize: '10px',
                  padding: '1px 5px',
                  borderRadius: '4px',
                  background: 'linear-gradient(135deg, #c0a050, #e8d48b)',
                  color: '#1a1a1a',
                  fontWeight: 600,
                }}>
                  Foil
                </span>
              )}
            </span>
          </div>
        </div>
      ),
    },
    {
      label: 'Points',
      key: 'points',
      render: (row) => (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          {row.points !== null && row.points > 0 ? (
            <Points points={row.points} type="small" />
          ) : (
            <div style={{ 
              position: 'relative',
              padding: '2px',
              borderRadius: '24px',
              background: 'linear-gradient(90deg, #f6e3bf, #634f21)',
              height: '18px',
              width: '65px'
            }}>
              <div style={{
                display: 'flex', 
                alignItems: 'center',
                position: 'absolute',
                top: '1px',
                bottom: '1px',
                left: '1px',
                right: '1px',
                padding: '1.5px',
                borderRadius: '24px',
                backgroundColor: 'rgba(0, 0, 0, 0.85)'
              }}>
                <img
                  src={`${AssetUrls.base}/assets/icons/profile/coin.svg`}
                  width={16}
                  height={16}
                  alt="coin-image"
                  style={{ paddingLeft: '1px' }}
                />
                <span style={{ 
                  flex: 1,
                  textAlign: 'center',
                  fontSize: '11px',
                  color: 'var(--gold)',
                  fontFamily: 'Georgia',
                  marginLeft: '2px',
                  marginRight: '2px',
                  paddingBottom: '2px'
                }}>???</span>
              </div>
            </div>
          )}
        </div>
      ),
    },
  ];

  const handleViewAllVersions = () => {
    // Navigate to search results page with exact card name match
    if (card?.name) {
      const encodedName = encodeURIComponent(card.name);
      navigate(`/search/results?name=${encodedName}`);
    }
  };

  const handlePrintRowClick = (row: PrintData) => {
    navigate(buildCardUrl(row));
  };

  const handleSetAvatar = async () => {
    try {
      // Get the Scryfall ID for the current card
      // The backend expects a Scryfall ID, not a URL
      let scryfallId = '';

      // Check if we have a Scryfall ID for the current card
      if (card?.scryfallId || (card as any)?.scryfallID) {
        scryfallId = card?.scryfallId || (card as any)?.scryfallID;
      } else {
        toast.error('Unable to set avatar - no card ID available');
        return;
      }

      if (!scryfallId) {
        toast.error('Unable to set avatar - invalid card ID');
        return;
      }

      // Backend accepts either just the Scryfall ID or the CDN URL
      // Try sending just the Scryfall ID as per backend documentation
      await updateAvatar({ avatarUrl: scryfallId }).unwrap();

      // Success is visible immediately in the UI, no toast needed
    } catch (error) {
      console.error('Failed to update avatar:', error);
      toast.error('Failed to update avatar');
    }
  };

  // Helper function to find the printing ID that matches current foil/language selection
  // The ViewCard page shows a specific printing, but the user may have changed foil/language dropdowns
  // We need to find the correct printing variant if it exists in the printings data
  const findMatchingPrintingId = (): number => {
    // Use numeric ID from the fetched card data (cardId in URL is now UUID)
    const currentCardId = card?.id || 0;

    // If no printings data available, use current cardId
    if (!printingsData || printingsData.length === 0) {
      return currentCardId;
    }

    // FIRST: Check if current card meets foil availability criteria
    // This prevents the loop from matching a different card first
    const currentPrinting = printingsData.find((p: any) => p.id === currentCardId) as any;
    if (currentPrinting) {
      const meetsAvailability = foilType === 'foil'
        ? currentPrinting.availableInFoil
        : currentPrinting.availableInNonFoil;

      if (meetsAvailability) {
        return currentCardId;
      }
    }

    // If current card doesn't meet criteria, try to find a printing that matches the selected foil type and language
    // Note: The API structure might vary, adjust field names as needed
    const matchingPrinting = printingsData.find((printing: any) => {
      // Match same set/collector number as current card
      const isSameSet = printing.setCode === card?.setCode ||
                        printing.set_code === (card as any)?.set_code ||
                        printing.setName === card?.setName ||
                        printing.set_name === card?.set_name;

      // Check language match
      const langMatch = printing.lang
        ? printing.lang.toLowerCase() === language.toLowerCase()
        : language === 'en'; // If no lang field, assume English

      // Check foil availability match
      let foilMatch = true;
      if (foilType === 'foil') {
        foilMatch = printing.availableInFoil === true;
      } else if (foilType === 'nonfoil') {
        foilMatch = printing.availableInNonFoil === true;
      }

      return isSameSet && langMatch && foilMatch;
    });

    // If we found a better match, use it; otherwise use current cardId
    const resultId = matchingPrinting ? matchingPrinting.id : currentCardId;

    return resultId;
  };

  const handleAddToHaveList = async () => {
    // Check if user is logged in - show login modal if not
    if (!currentUser) {
      setShowLoginModal(true);
      return;
    }

    try {
      // Find the correct printing ID based on selected foil/language
      const printingId = findMatchingPrintingId();

      // Add card to Have List with default quantity 1 and NM condition
      await addCardToHaveList({
        cardId: printingId,
        quantity: 1,
        condition: CardCondition.NEAR_MINT,
        foil: foilType === 'foil'
      }).unwrap();

      // Show success toast
      showCardAddedToHaveListSuccess(card?.name || 'Card', 1);

    } catch (error) {
      console.error('Failed to add card to Have List:', error);
      showError('Failed to add card to your Have List. Please try again.');
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="view-card-container">
        {/* Mobile Menu Overlay - click to close */}
        {isMobile && isMobileMenuOpen && (
          <div
            className="mobile-menu-overlay"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-hidden="true"
          />
        )}

        <LeftNavigationBar
          currentTab={currentTab}
          isMobile={isMobile}
          isMobileMenuOpen={isMobileMenuOpen}
          onMenuClose={() => setIsMobileMenuOpen(false)}
          isPublicMode={!currentUser}
          onNavigationAttempt={() => setShowLoginModal(true)}
          isAuthenticated={!!currentUser}
        />
        <div className="view-card-content">
          <div className="view-card-bg" />
          <div className="view-card-topnav">
            <TopNavigationBar
              title="View Card"
              isMobileMenuOpen={isMobileMenuOpen}
              onToggleMobileMenu={toggleMobileMenu}
            />
          </div>
          <div className="view-card-main">
            <div className="card-layout">
              <div className="loading-state">
                Loading card details...
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !cardData) {
    const errorStatus = (error as any)?.status;
    const errorMessage = (error as any)?.data?.message || (error as any)?.message || 'Unknown error';

    return (
      <div className="view-card-container">
        {/* Mobile Menu Overlay - click to close */}
        {isMobile && isMobileMenuOpen && (
          <div
            className="mobile-menu-overlay"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-hidden="true"
          />
        )}

        <LeftNavigationBar
          currentTab={currentTab}
          isMobile={isMobile}
          isMobileMenuOpen={isMobileMenuOpen}
          onMenuClose={() => setIsMobileMenuOpen(false)}
          isPublicMode={!currentUser}
          onNavigationAttempt={() => setShowLoginModal(true)}
          isAuthenticated={!!currentUser}
        />
        <div className="view-card-content">
          <div className="view-card-bg" />
          <div className="view-card-topnav">
            <TopNavigationBar
              title="View Card"
              isMobileMenuOpen={isMobileMenuOpen}
              onToggleMobileMenu={toggleMobileMenu}
            />
          </div>
          <div className="view-card-main">
            <div className="card-layout">
              <div className="error-state">
                <h2>Card not found</h2>
                <p>The card you're looking for doesn't exist or has been removed.</p>

                {/* Debug info - only visible in development */}
                {process.env.NODE_ENV === 'development' && (
                  <div style={{
                    marginTop: '20px',
                    padding: '10px',
                    background: '#333',
                    borderRadius: '5px',
                    fontSize: '12px',
                    textAlign: 'left',
                    borderTop: '2px solid #666'
                  }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Debug Info (dev only):</div>
                    <div>Card ID: {cardIdentifier}</div>
                    <div>Status: {errorStatus}</div>
                    <div>Message: {errorMessage}</div>
                  </div>
                )}

                <div style={{ marginTop: '20px' }}>
                  <MagicButton
                    label="Back to Search"
                    color="purple"
                    onPress={() => navigate('/search')}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check if this is a double-faced card
  const isDoubleFaced = card?.layout === 'transform' || card?.layout === 'modal_dfc';
  
  // Get the background image URL from the card data
  const getBackgroundImage = () => {
    // For non-EN cards, always use EN art_crop (localized art_crop often missing from CDN)
    if (card?.lang && card.lang !== 'en' && enScryfallId) {
      return AssetUrls.cards.getCardImage(enScryfallId, 'art_crop');
    }

    // For double-faced cards, use the first face's art_crop
    if (cardFaces && cardFaces.length > 0) {
      const firstFace = cardFaces[0];
      const backgroundUrl = firstFace.imageUris?.art_crop ||
             firstFace.image_uris?.art_crop ||
             firstFace.imageUris?.small ||
             firstFace.image_uris?.small ||
             null;
      // Convert Scryfall URLs to CDN URLs
      return backgroundUrl ? AssetUrls.cards.ensureCDNUrl(backgroundUrl) : null;
    }
    // For single-faced EN cards, use regular imageUris
    return card?.imageUris?.art_crop ||
           card?.image_uris?.art_crop ||
           card?.imageUris?.small ||
           card?.image_uris?.small ||
           null;
  };

  const backgroundImageUrl = getBackgroundImage();

  // Helper function to get the best OG image URL for social sharing
  const getOGImageUrl = (): string => {
    // Default fallback
    const defaultOGImage = 'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/logos/banner_social_share.png';

    // Try to get card image for OG tags
    // For double-faced cards, use the first face
    if (cardFaces && cardFaces.length > 0) {
      const firstFace = cardFaces[0];
      const faceImageUrl = firstFace.imageUris?.large ||
                firstFace.image_uris?.large ||
                firstFace.imageUris?.normal ||
                firstFace.image_uris?.normal;
      if (faceImageUrl) {
        return AssetUrls.cards.ensureCDNUrl(faceImageUrl);
      }
    }

    // If we have a Scryfall ID, construct CDN URL
    if (card?.scryfallId || (card as any)?.scryfallID) {
      const scryfallId = card?.scryfallId || (card as any)?.scryfallID;
      // Use large format for OG images (better quality for social previews)
      return AssetUrls.cards.getCardImage(scryfallId, 'large');
    }

    // Try to get from regular imageUris
    const cardImageUrl = card?.imageUris?.large ||
                        card?.imageUris?.normal ||
                        (card as any)?.image_uris?.large ||
                        (card as any)?.image_uris?.normal;
    if (cardImageUrl) {
      return AssetUrls.cards.ensureCDNUrl(cardImageUrl);
    }

    // Fallback to default TradeMagic OG image
    return defaultOGImage;
  };

  // Prepare SEO data
  const ogImageUrl = getOGImageUrl();
  const cardTitle = card?.name || 'Card';
  const cardSet = card?.setName || card?.set_name || '';
  const seoTitle = cardSet
    ? `${cardTitle} - ${cardSet} | TradeMagic`
    : `${cardTitle} | TradeMagic`;

  /**
   * Generate SEO-optimized description focused on trading value proposition
   */
  const generateEnhancedDescription = (): string => {
    if (!card) {
      return 'Trade Magic: The Gathering cards on TradeMagic. Free peer-to-peer trading platform with no fees.';
    }

    const parts: string[] = [];

    // Part 1: Main action - "Trade for [card name]"
    parts.push(`Trade for ${card.name} on TradeMagic.`);

    // Part 2: Set information (if available and not generic)
    if (cardSet && cardSet !== 'Unknown' && cardSet.trim() !== '') {
      parts.push(`${cardSet} edition.`);
    }

    // Part 3: Core value proposition
    parts.push('Free peer-to-peer trading platform.');

    // Part 4: Key differentiator
    parts.push('No fees, no hassle.');

    return parts.join(' ');
  };

  const seoDescription = generateEnhancedDescription();
  // While card data is loading or if the API returns no data, fall back to a
  // self-canonical (the current path). This guarantees /cards/{uuid} URLs always
  // emit a real canonical even on slow/error render paths instead of letting
  // SEOHelmet default to a homepage canonical, which Google reads as soft-404.
  const canonicalUrl = card
    ? `https://trademagic.gg${buildCardUrl(card)}`
    : `https://trademagic.gg${location.pathname}`;

  return (
    <>
      {/* SEO Components */}
      <SEOHelmet
        title={seoTitle}
        description={seoDescription}
        canonical={canonicalUrl}
        image={ogImageUrl}
        imageAlt={`${cardTitle} - Magic: The Gathering card`}
        type="article"
      />

      {card && (
        <BreadcrumbList
          breadcrumbs={[
            { name: 'Home', url: 'https://trademagic.gg' },
            { name: 'Search', url: 'https://trademagic.gg/search' },
            { name: cardTitle, url: canonicalUrl },
          ]}
        />
      )}

      <div className="view-card-container">
      {/* Mobile Menu Overlay - click to close */}
      {isMobile && isMobileMenuOpen && (
        <div
          className="mobile-menu-overlay"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      <LeftNavigationBar
        currentTab={currentTab}
        isMobile={isMobile}
        isMobileMenuOpen={isMobileMenuOpen}
        onMenuClose={() => setIsMobileMenuOpen(false)}
        isPublicMode={!currentUser}
        onNavigationAttempt={() => setShowLoginModal(true)}
        isAuthenticated={!!currentUser}
      />

      <div className="view-card-content">
        <div
          className="view-card-bg"
          style={backgroundImageUrl ? {
            backgroundImage: `url(${backgroundImageUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(110px)'
          } : undefined}
        />

        <div className="view-card-topnav">
          <TopNavigationBar
            title="View Card"
            isMobileMenuOpen={isMobileMenuOpen}
            onToggleMobileMenu={toggleMobileMenu}
          />
        </div>

        <div className="view-card-main">
          <div className="card-layout">
            {/* LEFT COLUMN */}
            <div className="card-col card-col-left">
              {/* Language Dropdown */}
              <FormControl
                fullWidth
                sx={{ maxWidth: 380, mb: 1, position: 'relative' }}
              >
                <Select
                  value={language}
                  onChange={(e) => {
                    const newLang = e.target.value;
                    setLanguage(newLang);
                    // Navigate to the language variant using scryfallId from availableLanguages
                    if (newLang !== card?.lang) {
                      const selectedOption = availableLanguages.find((l: LanguageOption) => l.code === newLang);
                      if (selectedOption?.scryfallId) {
                        navigate(`/cards/${selectedOption.scryfallId}`, { replace: true });
                      }
                    }
                  }}
                  displayEmpty
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        bgcolor: '#181818',
                        color: 'white',
                        fontFamily: 'Plus Jakarta Sans',
                        fontSize: '11px',
                        borderRadius: '8px',
                        border: '1px solid #323232',
                      },
                    },
                  }}
                  sx={{
                    height: '38px',
                    borderRadius: '8px',
                    backgroundColor: '#000',
                    color: 'white',
                    fontFamily: 'Plus Jakarta Sans',
                    fontSize: '16px',
                    border: '1px solid #323232',
                    pl: '30px',
                    '& .MuiSelect-icon': {
                      color: 'white',
                    },
                  }}
                  inputProps={{ sx: { padding: '0 14px' } }}
                >
                  {availableLanguages.map((lang: LanguageOption) => (
                    <MenuItem key={lang.code} value={lang.code}>
                      {lang.name}
                    </MenuItem>
                  ))}
                </Select>

                {/* Language icon */}
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '12px',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src={`${AssetUrls.base}/assets/icons/dropdowns/Icons-dropdown-Language-45px.svg`}
                    alt="Language Icon"
                    style={{ width: 20, height: 21 }}
                  />
                </div>
              </FormControl>

              {/* Foil Dropdown - Show when there are multiple foil options OR a ★ counterpart exists */}
              {(availableFoilTypes.length > 1 || starPair) && (
                <FormControl
                  fullWidth
                  sx={{ maxWidth: 380, mb: 2, position: 'relative' }}
                >
                  <Select
                    value={starPair ? (starPair.isStar ? 'foil' : 'nonfoil') : foilType}
                    onChange={(e) => {
                      if (starPair) {
                        // ★ pair: navigate to the paired printing
                        navigate(buildCardUrl({ ...starPair.paired, name: card.name }));
                      } else {
                        setFoilType(e.target.value);
                      }
                    }}
                    displayEmpty
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          bgcolor: '#181818',
                          color: 'white',
                          fontFamily: 'Plus Jakarta Sans',
                          fontSize: '11px',
                          borderRadius: '8px',
                          border: '1px solid #323232',
                        },
                      },
                    }}
                    sx={{
                      height: '38px',
                      borderRadius: '8px',
                      backgroundColor: '#000',
                      color: 'white',
                      fontFamily: 'Plus Jakarta Sans',
                      fontSize: '16px',
                      border: '1px solid #323232',
                      pl: '30px',
                      '& .MuiSelect-icon': {
                        color: 'white',
                      },
                    }}
                    inputProps={{ sx: { padding: '0 14px' } }}
                  >
                    {starPair ? (
                      [
                        <MenuItem key="nonfoil" value="nonfoil">Non-Foil</MenuItem>,
                        <MenuItem key="foil" value="foil">Foil</MenuItem>
                      ]
                    ) : (
                      availableFoilTypes.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))
                    )}
                  </Select>

                  {/* Foil icon */}
                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '12px',
                      transform: 'translateY(-50%)',
                      pointerEvents: 'none',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <img
                      src={`${AssetUrls.base}/assets/icons/dropdowns/Icons-Dropdown-Foil-45pxb.png`}
                      alt="Foil Icon"
                      style={{ width: 20, height: 21 }}
                    />
                  </div>
                </FormControl>
              )}

              {/* Prints Table */}
              {!isLoadingPrintings && printRows.length > 0 && (
                <Table
                  columns={printColumns}
                  rows={printRows.slice(0, 10)} // Show first 10 printings
                  onRowClick={handlePrintRowClick}
                  clickableRows={true}
                  footer={
                    printRows.length > 10 && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          paddingLeft: '4px',
                          cursor: 'pointer',
                        }}
                        onClick={handleViewAllVersions}
                      >
                        <img
                          src={`${AssetUrls.base}/assets/icons/ViewCard/arrow-right.svg`}
                          alt="Arrow Right"
                          style={{ width: 14, height: 14 }}
                        />
                        View All {printRows.length} Versions
                      </div>
                    )
                  }
                />
              )}
            </div>

            {/* CENTER COLUMN */}
            <div className="card-col card-col-center">
              <div className="card-preview">
                <FoilCardImage
                  src={(() => {
                    // For double-faced cards, show the selected face
                    if (isDoubleFaced && cardFaces && cardFaces[displayedFaceIndex]) {
                      const currentFace = cardFaces[displayedFaceIndex];

                      // Try to get image from the current face
                      const faceImages = [
                        currentFace?.image_uris?.normal,
                        currentFace?.image_uris?.large,
                        currentFace?.imageUris?.normal,
                        currentFace?.imageUris?.large,
                        // Fallback to properties directly on the face
                        currentFace?.normal,
                        currentFace?.large,
                      ];

                      const validFaceImage = faceImages.find(img => img && img !== '');
                      if (validFaceImage) {
                        // Convert Scryfall URLs to CDN URLs for double-faced cards
                        return AssetUrls.cards.ensureCDNUrl(validFaceImage);
                      }
                    }
                    
                    // For non-EN cards with no localized CDN image, show EN version immediately
                    if (useEnFallback) {
                      return AssetUrls.cards.getCardImage(enScryfallId!, 'normal');
                    }

                    // If we have a Scryfall ID, use our CDN with fallback support
                    if (displayScryfallId) {
                      return AssetUrls.cards.getCardImage(displayScryfallId, 'normal');
                    }
                    
                    // Default image selection for single-faced cards or fallback
                    const possibleImages = [
                      card?.imageUris?.normal,
                      card?.imageUris?.large,
                      (card as any)?.image_uris?.normal,
                      (card as any)?.image_uris?.large,
                      // For double-faced cards, try the first face as fallback
                      cardFaces?.[0]?.image_uris?.normal,
                      cardFaces?.[0]?.image_uris?.large,
                      cardFaces?.[0]?.imageUris?.normal,
                      cardFaces?.[0]?.imageUris?.large,
                    ];
                    
                    const validImage = possibleImages.find(img => img && img !== '');
                    return validImage || 'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/Search/image-coming-soon.png';
                  })()}
                  alt={cardFaces && isDoubleFaced && cardFaces[displayedFaceIndex]
                    ? cardFaces[displayedFaceIndex].name || card?.name || 'Card'
                    : card?.name || 'Card'}
                  foil={foilType === 'foil' || foilType === 'etched'}
                  borderRadius="12px"
                  style={{
                    width: '100%',
                    height: '100%',
                  }}
                  onError={(e) => {
                    const img = e.currentTarget;
                    const currentSrc = img.src;

                    // Prevent infinite loops - check if we've already tried placeholder or EN fallback
                    if (currentSrc.includes('image-coming-soon.png')) {
                      return;
                    }

                    // If we're already showing the EN fallback and it fails, go to placeholder
                    if (localizedImageFailed) {
                      img.src = 'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/Search/image-coming-soon.png';
                      return;
                    }

                    const placeholderUrl = 'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/Search/image-coming-soon.png';

                    // Try fallback formats: normal -> small -> large -> EN fallback or placeholder
                    const scryfallId = card?.scryfallId || (card as any)?.scryfallID;
                    if (scryfallId && !currentSrc.includes('image-coming-soon')) {
                      if (currentSrc.includes('/normal/')) {
                        img.src = AssetUrls.cards.getCardImage(scryfallId, 'small');
                      } else if (currentSrc.includes('/small/')) {
                        img.src = AssetUrls.cards.getCardImage(scryfallId, 'large');
                      } else if (currentSrc.includes('/large/')) {
                        // All CDN formats exhausted — try EN fallback for non-EN cards
                        if (card?.lang && card.lang !== 'en' && enScryfallId) {
                          setLocalizedImageFailed(true);
                        } else {
                          img.src = placeholderUrl;
                        }
                      } else {
                        if (card?.lang && card.lang !== 'en' && enScryfallId) {
                          setLocalizedImageFailed(true);
                        } else {
                          img.src = placeholderUrl;
                        }
                      }
                    } else {
                      img.src = placeholderUrl;
                    }
                  }}
                />
                
                {/* Localized image not available banner */}
                {useEnFallback && card?.lang && (
                  <div className="localized-image-banner">
                    <div className="localized-image-badge">
                      {getLanguageDisplayName(card.lang) || card.lang.toUpperCase()}
                    </div>
                    <div className="localized-image-text">
                      Localized Image Not Available
                    </div>
                  </div>
                )}

                {/* Hover overlay with Set Avatar button */}
                <div className="card-preview-overlay">
                  <button 
                    className="set-avatar-button"
                    onClick={handleSetAvatar}
                    disabled={isUpdatingAvatar}
                  >
                    {isUpdatingAvatar ? 'Setting...' : 'Set Avatar'}
                  </button>
                </div>
              </div>

              {/* Value Label */}
              <div className="card-value-label">
                {(() => {
                  const currentPrice = getCurrentPrice();
                  const badge = currentPrice !== null && currentPrice > 0 ? (
                    <Points points={currentPrice} type="big" />
                  ) : (
                    <div style={{
                      position: 'relative',
                      padding: '2px',
                      borderRadius: '24px',
                      background: 'linear-gradient(90deg, #f6e3bf, #634f21)',
                      height: '32px',
                      width: '96px'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        position: 'absolute',
                        top: '1px',
                        bottom: '1px',
                        left: '1px',
                        right: '1px',
                        padding: '2px',
                        borderRadius: '24px',
                        backgroundColor: 'rgba(0, 0, 0, 0.85)'
                      }}>
                        <img
                          src={`${AssetUrls.base}/assets/icons/profile/coin.svg`}
                          width={21.89}
                          height={21.89}
                          alt="coin-image"
                          style={{ paddingLeft: '2px' }}
                        />
                        <span style={{
                          flex: 1,
                          textAlign: 'center',
                          fontSize: '14px',
                          color: 'var(--gold)',
                          fontFamily: 'Georgia',
                          marginLeft: '2px',
                          marginRight: '2px'
                        }}>???</span>
                      </div>
                    </div>
                  );

                  if (isAdmin && card?.name) {
                    return (
                      <Link
                        to={`/admin/cards?search=${encodeURIComponent(card.name)}&filter=all&set=${encodeURIComponent(card?.setCode || card?.set_code || '')}`}
                        style={{ textDecoration: 'none', opacity: 1, transition: 'opacity 0.2s' }}
                        onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.8'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
                        title="Edit pricing in admin panel"
                      >
                        {badge}
                      </Link>
                    );
                  }

                  return badge;
                })()}
                {/* Foil/Etched indicator when only one finish type exists and dropdown is hidden */}
                {availableFoilTypes.length === 1 && availableFoilTypes[0].value !== 'nonfoil' && (
                  <span style={{
                    color: '#c0a050',
                    fontSize: '12px',
                    fontStyle: 'italic',
                    marginTop: '4px',
                  }}>
                    {availableFoilTypes[0].label}
                  </span>
                )}
              </div>

              {/* Action Buttons */}
              <div className="card-actions">
                {isDoubleFaced && (
                  <div className="card-actions-row">
                    <MagicButton
                      icon={<SwapHoriz />}
                      label="Transform"
                      color="dark-grey"
                      onPress={() => {
                        setDisplayedFaceIndex(displayedFaceIndex === 0 ? 1 : 0);
                      }}
                      fullWidth
                    />
                  </div>
                )}
                <div className="card-actions-row">
                  <MagicButton
                    icon={<img src={AssetUrls.icons.misc.have} alt="Have" style={{ width: '14px', height: '14px' }} />}
                    label="Have"
                    color="dark-grey"
                    onPress={handleAddToHaveList}
                    isLoading={isAddingToHaveList}
                  />
                  <MagicButton
                    icon="heart"
                    label="Want"
                    color="purple"
                    onPress={() => {
                      // Navigate to view-card-printings page with card ID for exact matching
                      if (card?.name) {
                        const encodedName = encodeURIComponent(card.name);

                        // Pass through navigation state if we came from search
                        const navigationState = fromSearch && returnPath
                          ? { from: 'search', returnPath }
                          : undefined;

                        const langParam = language && language !== 'en' ? `&lang=${language}` : '';
                        navigate(`/view-card-printings/${encodedName}?cardId=${cardIdentifier}${langParam}`, {
                          state: navigationState
                        });
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN */}
            <div className="card-col card-col-right">
              <div className="oracle-block">
                {/* Card Details */}
                {!isDoubleFaced ? (
                  // Single-faced card
                  <>
                    {(() => {
                      const { primary, secondary } = pickAltNames({
                        name: card?.name ?? '',
                        printedName: card?.printedName,
                        flavorName: (card as any)?.flavorName,
                        lang: card?.lang,
                      });
                      if (!primary) {
                        return <p className="oracle-heading">{card?.name}</p>;
                      }
                      return (
                        <>
                          <p className="oracle-heading">{primary}</p>
                          {secondary && <p className="oracle-heading-secondary">{secondary}</p>}
                          <p className="oracle-heading-secondary">{card?.name}</p>
                        </>
                      );
                    })()}
                    {(card?.setName || card?.set_name) && (
                      <div className="oracle-set-info">
                        <SetIcon
                          setIconUrl={(card as any)?.setIconUrl}
                          setName={card?.setName || card?.set_name}
                          size="lg"
                          className="oracle-set-icon"
                        />
                        <Link
                          to={`/search/results?setName=${encodeURIComponent(card?.setName || card?.set_name)}&exactSetName=true`}
                          className="oracle-set-link"
                        >
                          {card?.setName || card?.set_name}
                        </Link>
                        {(card?.collectorNumber || card?.collector_number) && (
                          <span style={{ marginLeft: 8 }}>
                            #{card?.collectorNumber || card?.collector_number}
                          </span>
                        )}
                      </div>
                    )}
                    <p className="oracle-subheading">
                      {card?.lang !== 'en' && card?.printedTypeLine ? card.printedTypeLine : card?.typeLine}
                    </p>
                    {(card?.lang !== 'en' && card?.printedText ? card.printedText : card?.oracleText) && (
                      <p dangerouslySetInnerHTML={{
                        __html: replaceManaSymbols(
                          (card?.lang !== 'en' && card?.printedText ? card.printedText : card?.oracleText) || '',
                          card?.manaSymbols ? new Map(card.manaSymbols.map((s: {symbol: string; url: string}) => [s.symbol, s.url])) : undefined
                        )
                      }} />
                    )}
                    {(card?.power || card?.toughness) && (
                      <p className="oracle-power">{card?.power}/{card?.toughness}</p>
                    )}
                    {card?.loyalty && (
                      <p className="oracle-power">Loyalty: {card?.loyalty}</p>
                    )}
                  </>
                ) : (
                  // Double-faced card
                  cardFaces?.map((face: any, index: number) => (
                    <div key={index}>
                      {card?.lang !== 'en' && face.printed_name ? (
                        <>
                          <p className="oracle-heading">{face.printed_name}</p>
                          <p className="oracle-heading-secondary">{face.name}</p>
                        </>
                      ) : (
                        <p className="oracle-heading">{face.name}</p>
                      )}
                      {index === 0 && (card?.setName || card?.set_name) && (
                        <div className="oracle-set-info">
                          <SetIcon
                            setIconUrl={(card as any)?.setIconUrl}
                            setName={card?.setName || card?.set_name}
                            size="sm"
                            className="oracle-set-icon"
                          />
                          <Link
                            to={`/search/results?setName=${encodeURIComponent(card?.setName || card?.set_name)}&exactSetName=true`}
                            className="oracle-set-link"
                          >
                            {card?.setName || card?.set_name}
                          </Link>
                          {(card?.collectorNumber || card?.collector_number) && (
                            <span style={{ marginLeft: 8 }}>
                              #{card?.collectorNumber || card?.collector_number}
                            </span>
                          )}
                        </div>
                      )}
                      <p className="oracle-subheading">
                        {card?.lang !== 'en' && face.printed_type_line ? face.printed_type_line : face.type_line}
                      </p>
                      {(card?.lang !== 'en' && face.printed_text ? face.printed_text : face.oracle_text) && (
                        <p dangerouslySetInnerHTML={{
                          __html: replaceManaSymbols(
                            (card?.lang !== 'en' && face.printed_text ? face.printed_text : face.oracle_text),
                            face.manaSymbols ? new Map(face.manaSymbols.map((s: any) => [s.symbol, s.url])) : undefined
                          )
                        }} />
                      )}
                      {(face.power || face.toughness) && (
                        <p className="oracle-power">{face.power}/{face.toughness}</p>
                      )}
                      {face.loyalty && (
                        <p className="oracle-power">Loyalty: {face.loyalty}</p>
                      )}
                      {index < cardFaces.length - 1 && <br />}
                    </div>
                  ))
                )}

                {card?.artist && (
                  <p className="oracle-subheading">
                    Illustrated by{' '}
                    <Link
                      to={`/search/results?artist=${encodeURIComponent(card.artist)}`}
                      className="oracle-illustrator"
                    >
                      {card?.artist}
                    </Link>
                  </p>
                )}
                {card?.reserved === true && (
                  <p className="oracle-subheading">
                    Part of the{' '}
                    <Link to="/search/results?reserved=true" className="oracle-illustrator">
                      Reserved List
                    </Link>
                    .
                  </p>
                )}

                {/* Community stat line — card-scoped (all printings). Compact
                    number-first format ("[icon] N Haves"), one template for all
                    values (no pluralization), branded S3 iconography (the same
                    have/heart/sending icons the rest of the site uses).
                    Owned/traded segments are gated on field presence purely as
                    a deploy-order guard; once the backend ships them the line
                    always renders, zeros included. */}
                {card && (
                  <div className="oracle-stats">
                    {typeof card.ownersCount === 'number' && (
                      <span className="oracle-stat">
                        <img src={AssetUrls.icons.misc.have} alt="" className="oracle-stat__icon" />
                        <strong>{card.ownersCount}</strong> Haves
                      </span>
                    )}
                    <span className="oracle-stat">
                      <img
                        src={AssetUrls.icons.misc.heart}
                        alt=""
                        className="oracle-stat__icon oracle-stat__icon--heart"
                      />
                      {isLoadingWanters || wantedByCount === undefined ? (
                        <>… Wants</>
                      ) : (
                        <>
                          <strong>{wantedByCount}</strong> Wants
                        </>
                      )}
                    </span>
                    {typeof card.tradesCount === 'number' && (
                      <span className="oracle-stat">
                        <img
                          src={AssetUrls.icons.navigation.sending}
                          alt=""
                          className="oracle-stat__icon oracle-stat__icon--sending"
                        />
                        <strong>{card.tradesCount}</strong> Traded
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Dark table panel — renders as its own container flush against the bottom of
            .view-card-main. Returns null (no element) when there are zero wanters, which
            lets .view-card-main keep its rounded bottom corners via :has(). */}
        {card?.oracleId && (
          <CardWantersTable
            oracleId={card.oracleId}
            cardName={card?.name || 'this card'}
            isLoggedIn={!!currentUser}
            onRequireLogin={handleRequireLogin}
          />
        )}

        <div className="view-card-footer">
          <Footer />
        </div>
      </div>

      {/* Login prompt modal for unauthenticated users */}
      <LoginPromptModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        collectionOwnerUsername={card?.name || 'this card'}
      />
    </div>
    </>
  );
}